import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';
import { Toaster } from 'sonner';
import { PageProvider } from '@hero/web-context';
import {
  AppSidebar,
  AgentEntityDrawers,
  ChatPanelLauncher,
  OrderCreateButton,
  SidebarProvider,
  SubscriptionRenewalBadge,
  TopBar,
} from '@hero/web-components';
import { appRoutes, useNav } from '@hero/web-hooks';
import { useFeature } from '@hero/web-context';
import { useOrderAgainConfig } from '@hero/web-hooks';
import { useSidebarNavigation } from '../data/staticData';

export default function DashboardLayout() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { i18n } = useTranslation();
  // Auth-v2: sidebar gates driver-only items via session-scoped
  // `useCurrentUser().isDriver` (see `resolveDriverSession`).
  const navigation = useSidebarNavigation();
  const location = useLocation();
  const { navigate } = useNav();
  /**
   * Hide the create-order CTA while the user is *on* the create-order
   * page (otherwise the button would just navigate them to themselves).
   * Pinned to the typed `new_order` route so renaming the URL stays a
   * single-source-of-truth change in the navigation registry.
   */
  const isOrderFlow = location.pathname.startsWith(appRoutes.new_order.path);
  const showOrderCreate = !isOrderFlow;
  const { isEnabled: aiAgentEnabled } = useFeature('ai_agent_module', 'sub_chat_panel');
  /**
   * Hide the floating launcher when the user is *on* the full-page AI
   * surface — otherwise we'd render two chat panels (the page + the
   * pill-launched drawer) on top of each other. Same single-source
   * pattern used for `showOrderCreate` above.
   */
  const isOnAIPage = location.pathname.startsWith(appRoutes.ai_agent.path);
  const orderAgainConfig = useOrderAgainConfig();

  return (
    <PageProvider>
      <SidebarProvider
        defaultOpen={true}
        open={!isCollapsed}
        onOpenChange={open => setIsCollapsed(!open)}
        dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
        <div className="grid grid-cols-[auto,1fr] grid-rows-[auto,1fr] w-full">
          <header className="px-4 sm:px-6 h-[80px] border-b bg-white flex justify-between items-center sticky top-0 z-50 col-start-2">
            <TopBar
              isCollapsed={isCollapsed}
              onToggleSidebar={() => setIsCollapsed(prev => !prev)}
              actions={
                <>
                  <SubscriptionRenewalBadge />
                  {showOrderCreate ? (
                    <OrderCreateButton
                      enableDynamicExpanding
                      fullWidth={false}
                      onClick={() => navigate('new_order')}
                    />
                  ) : null}
                </>
              }
            />
          </header>
          <aside className="row-span-full">
            <AppSidebar
              isCollapsed={isCollapsed}
              navigationItems={navigation}
              showStagingBadge={!import.meta.env.PROD}
            />
          </aside>

          <main className="overflow-x-hidden px-4 py-5 sm:px-6">
            <Outlet />

            <Toaster
              position="bottom-center"
              dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
              toastOptions={{
                classNames: {
                  error: 'subtitle2 text-black',
                  success: 'subtitle2 text-black',
                  warning: 'subtitle2 text-black',
                  info: 'subtitle2 text-black',
                },
              }}
              icons={{
                success: <img src="/images/toastIcons/success.svg" alt="success" />,
                info: <img src="/images/toastIcons/info.svg" alt="info" />,
                warning: <img src="/images/toastIcons/warning.svg" alt="warning" />,
                error: <img src="/images/toastIcons/error.svg" alt="error" />,
              }}
            />
          </main>
        </div>
        {/* TODO: re-enable the floating AI chat launcher once we're
            ready to ship it network-wide. Hidden for now per product
            request; the dedicated `/ai-agent` page still works.
            {aiAgentEnabled && !isOnAIPage && <ChatPanelLauncher />} */}
        <AgentEntityDrawers displayForUser="carrier" orderAgainConfig={orderAgainConfig} />
      </SidebarProvider>
    </PageProvider>
  );
}
